import React, { useState, useRef } from "react";
import axios from "axios";
import "./ConnectivityTest.css"; // Import the CSS file

// Constants for URLs and magic numbers
const urlsToCheck = [
  { appName: 'Application', url: process.env.REACT_APP_FRONTEND_URL },
  { appName: 'Gateway', url: process.env.REACT_APP_API_URL },
  { appName: 'Console', url: `${process.env.REACT_APP_GUACAMOLE_URL}/#/` },
  { appName: 'Report', url: process.env.REACT_APP_GRAFANA_URL },
  { appName: 'CodeShare', url: process.env.REACT_APP_CODESHARE_URL },
  { appName: 'Compiler', url: process.env.REACT_APP_COMPILER_URL },
];

const desiredOrder = ['Application', 'Gateway', 'CodeShare', 'Compiler', 'Console', 'Report'];

// Spinner Component (can be in its own file)
const Spinner = () => (
  <div className="spinner">
    <div className="spinner-dot"></div>
  </div>
);

const ConnectivityTest = () => {
  const [connectivityStatus, setConnectivityStatus] = useState([]);
  const [activeTest, setActiveTest] = useState(null);
  const [downloadSpeed, setDownloadSpeed] = useState(null);
  const [uploadSpeed, setUploadSpeed] = useState(null);
  const [ping, setPing] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  // A more robust checkConnectivity function
  const checkConnectivity = async (appName, url) => {
    try {
      // Use a AbortController for a cancellable fetch with a timeout
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 5000); // 5-second timeout

      const response = await axios.get(url, { signal: controller.signal });
      clearTimeout(id);
      return { appName, success: true, status: response.status, statusText: response.statusText };
    } catch (error) {
      // Check for timeout or network errors
      if (axios.isCancel(error) || error.message.includes('timeout')) {
        return { appName, success: false, status: 'Timed out', statusText: 'Request timed out.' };
      } else if (error.code === 'ERR_NETWORK' || (error.response && error.response.status === 0)) {
        // Fallback to iframe for CORS-blocked URLs
        return new Promise((resolve) => {
          const iframe = document.createElement('iframe');
          iframe.src = url;
          iframe.style.display = 'none';
          document.body.appendChild(iframe);

          const timeoutId = setTimeout(() => {
            document.body.removeChild(iframe);
            resolve({ appName, success: false, status: 'Iframe Timeout', statusText: 'Iframe failed to load within timeout.' });
          }, 7000);

          iframe.onload = () => {
            clearTimeout(timeoutId);
            document.body.removeChild(iframe);
            resolve({ appName, success: true, status: '200 (via iframe)', statusText: 'Site reachable (via iframe)' });
          };

          iframe.onerror = () => {
            clearTimeout(timeoutId);
            document.body.removeChild(iframe);
            resolve({ appName, success: false, status: 'Iframe Error', statusText: 'Network or domain error (via iframe)' });
          };
        });
      } else if (error.response) {
        return { appName, success: false, status: error.response.status, statusText: `Server error: ${error.response.statusText}` };
      } else {
        return { appName, success: false, status: 'Error', statusText: `Error: ${error.message}` };
      }
    }
  };

  const handleCheckConnectivity = async () => {
    setActiveTest("connectivity");
    setIsTesting(true);
    setConnectivityStatus([]);

    const promises = urlsToCheck.map(item => checkConnectivity(item.appName, item.url));
    const results = await Promise.all(promises);
    setConnectivityStatus(results);
    setIsTesting(false);
  };

  const checkInternetSpeed = async () => {
    setActiveTest("speed");
    setIsTesting(true);
    setDownloadSpeed(null);
    setUploadSpeed(null);
    setPing(null);

    try {
      const testFileUrl = `${process.env.REACT_APP_FRONTEND_URL}/static/test_file.bin`;
      const pingUrl = `${process.env.REACT_APP_FRONTEND_URL}/get`;
      const uploadUrl = `${process.env.REACT_APP_FRONTEND_URL}/post`;

      // Download Speed Test
      const downloadStart = performance.now();
      const response = await fetch(testFileUrl, { cache: "no-store" });
      const buffer = await response.arrayBuffer();
      const downloadEnd = performance.now();
      const fileSizeInBits = buffer.byteLength * 8;
      const downloadDuration = (downloadEnd - downloadStart) / 1000;
      const downloadSpeedMbps = (fileSizeInBits / downloadDuration) / (1024 * 1024);
      setDownloadSpeed(downloadSpeedMbps.toFixed(2));

      // Ping (Latency) Test
      const pingStart = performance.now();
      await fetch(pingUrl, { cache: "no-store", method: "HEAD" });
      const pingEnd = performance.now();
      setPing(Math.round(pingEnd - pingStart));

      // Upload Speed Test
      const uploadData = new Blob([new ArrayBuffer(500000)]);
      const uploadStart = performance.now();
      await fetch(uploadUrl, {
        method: "POST",
        body: uploadData,
        cache: "no-store",
      });
      const uploadEnd = performance.now();
      const uploadDuration = (uploadEnd - uploadStart) / 1000;
      const uploadSpeedMbps = (uploadData.size * 8 / uploadDuration) / (1024 * 1024);
      setUploadSpeed(uploadSpeedMbps.toFixed(2));

    } catch (error) {
      console.error("Error checking internet speed:", error);
      setDownloadSpeed("Error");
      setUploadSpeed("Error");
      setPing("Error");
    } finally {
      setIsTesting(false);
    }
  };

  const sortedStatus = [...connectivityStatus].sort((a, b) => {
    const indexA = desiredOrder.indexOf(a.appName);
    const indexB = desiredOrder.indexOf(b.appName);
    return indexA - indexB;
  });

  const hasErrors = sortedStatus.some(item => !item.success);

  const renderConnectivityCheck = () => {
    if (activeTest !== "connectivity") return null;

    return (
      <div className="test-results-container">
        <table>
          <tbody>
            {sortedStatus.map((item, index) => (
              <tr key={index}>
                <td>{item.appName}</td>
                <td className={item.success ? "status-success" : "status-failure"}>
                  {item.success ? "✔" : "✘"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasErrors && (
          <p className="error-note">
            NOTE: If there is any issue, contact support@zippyops.com
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="connectivity-test-container">
      <div className="content">
        <div className="button-group">
          <button
            onClick={handleCheckConnectivity}
            disabled={isTesting}
            className="test-button connectivity-button"
          >
            {isTesting && activeTest === "connectivity" ? (
              <>
                <Spinner />
                Testing...
              </>
            ) : (
              'Connectivity Test'
            )}
          </button>

          <button
            onClick={checkInternetSpeed}
            disabled={isTesting}
            className="test-button speed-button"
          >
            {isTesting && activeTest === "speed" ? 'Testing...' : 'Speed Test'}
          </button>
        </div>
        {/* <div className="scrollable-results-wrapper"> */}
        {activeTest === "speed" && (
          <div className="test-results-container speed-results">
            <h3>Connectivity Speed Test</h3>
            {isTesting ? (
              <p>Testing your Connectivity speed...</p>
            ) : (
              <div className="speed-metrics">
                <div className="metric-card download">
                  <h4>Download Speed</h4>
                  <p className="value">{downloadSpeed === null ? "--" : downloadSpeed}</p>
                  <p className="unit">Mbps</p>
                </div>
                <div className="metric-card upload">
                  <h4>Upload Speed</h4>
                  <p className="value">{uploadSpeed === null ? "--" : uploadSpeed}</p>
                  <p className="unit">Mbps</p>
                </div>
                <div className="metric-card ping">
                  <h4>Ping</h4>
                  <p className="value">{ping === null ? "--" : ping}</p>
                  <p className="unit">ms</p>
                </div>
              </div>
            )}
          </div>
        )}
        {renderConnectivityCheck()}
        {/* </div> */}
        </div>
    </div>
  );
};

export default ConnectivityTest;